import classNames from 'classnames';
import React from 'react';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import {PartJSON} from 'biojs/Part';
import {SchemaTag, Tag} from 'folder-items/FileConstants';
import {SchemaValidationErrorRecord} from 'schemas/SchemaValidationConstants';
import * as TagSchemaUtil from 'tag-schema/util/TagSchemaUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import {ElementKeyboardEvent, Optional} from 'util/Types';

import SchemaFieldValueError from './SchemaFieldValueError';
import TagValueEditor from './TagValueEditor';
import TagValueViewer from './TagValueViewer';

export interface TagValueCellProps {
  errors?: Array<SchemaValidationErrorRecord> | null;
  fileId?: string;
  itemLinkAllowed: boolean;
  isEditing: boolean;
  onKeyDown: (e: ElementKeyboardEvent) => void;
  onUpdateTag: (tagIndex: number, newValue: Tag) => void;
  sequenceParts: Optional<Array<PartJSON>>;
  schemaFieldSelectorId?: string;
  shouldAutoFocus: boolean;
  tagIndex: number;
  tag: SchemaTag;
}

/**
 * Component for viewing and editing a field value as part of the metadata editor, including errors
 * and a "trash" button to remove the field in some cases.
 */
class TagValueCell extends React.Component<TagValueCellProps> {
  private getDefaultTagValue = (): SchemaTag => ({
    ...this.props.tag,
    name: this.props.tag.name || '',
    value: this.props.tag.value || '',
    url: this.props.tag.url || '',
  });

  private handleChangeValue = (newValue: Tag): void => {
    this.props.onUpdateTag(this.props.tagIndex, newValue);
  };

  private renderErrors(errors: Array<SchemaValidationErrorRecord>): Array<JSX.Element> {
    if (BenchlingConfig.REGISTRY_ENABLE_MULTIPLE_ERROR_RECORDS_PER_FIELD) {
      // Group all messages by error label so a field can surface every error for a label (e.g. a field
      // that fails multiple configurable validation rules), rather than just the first. Identical messages
      // are still de-duplicated (e.g. a field linking to 2 files outside the registry produces the same
      // message twice), since repeating the same text is confusing.
      return Object.entries(_.groupBy(errors, 'errorLabel')).map(([errorLabel, labelErrors]) => (
        <SchemaFieldValueError
          errorLabel={errorLabel}
          schemaFieldRenderData={labelErrors[0].field!}
          messages={[...new Set(labelErrors.map((error) => error.message))]}
          key={errorLabel}
        />
      ));
    }
    // Filter out duplicate errors (e.g. a field linking to 2 files outside the registry), since showing
    // the same error to the user is confusing.
    return (
      // eslint-disable-next-line no-restricted-syntax
      _.uniq(errors, 'errorLabel').map((error) => (
        <SchemaFieldValueError
          errorLabel={error.errorLabel}
          schemaFieldRenderData={error.field!}
          messages={[error.message]}
          key={error.errorLabel}
        />
      ))
    );
  }

  override render(): JSX.Element {
    const isItemLinkField =
      this.props.tag.tagSchemaField &&
      TagSchemaUtil.isItemLinkFieldType(this.props.tag.tagSchemaField.fieldType);
    return (
      <div
        className={classNames('u-width-full', {
          'u-cursor-text': !this.props.isEditing && !isItemLinkField,
        })}
        {...getTestProps({component: 'TagValueCell', element: 'root', key: this.props.tag.name})}
      >
        {this.props.isEditing ? (
          <div className="metadata-tagValueInput u-width-full">
            <TagValueEditor
              autoFocus={this.props.shouldAutoFocus}
              fileId={this.props.fileId}
              itemLinkAllowed={this.props.itemLinkAllowed}
              onChange={this.handleChangeValue}
              onKeyDown={this.props.onKeyDown}
              schemaFieldSelectorId={this.props.schemaFieldSelectorId}
              sequenceParts={this.props.sequenceParts}
              tag={this.getDefaultTagValue()}
            />
          </div>
        ) : (
          <TagValueViewer tag={this.props.tag} />
        )}
        {this.props.errors && this.renderErrors(this.props.errors)}
      </div>
    );
  }
}

export default TagValueCell;
